import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import {
  useMemberProfile,
  useMemberPayments,
  useMemberOrders,
  useMemberTickets,
  useMemberCoupons,
  useMemberAddresses,
} from '../hooks/useMember.js';
import { SubscriptionTab } from '../components/member/SubscriptionTab.js';
import { PaymentTab } from '../components/member/PaymentTab.js';
import { OrderTab } from '../components/member/OrderTab.js';
import { TicketTab } from '../components/member/TicketTab.js';
import { CouponTab } from '../components/member/CouponTab.js';
import { AddressTab } from '../components/member/AddressTab.js';
import { STATUS_STYLES } from '../lib/member-constants.js';

type TabKey = 'sub' | 'pay' | 'order' | 'ticket' | 'coupon' | 'address';

/** @graph-connects none */
const TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'sub', labelKey: 'memberDetail.tabSubscription' },
  { key: 'pay', labelKey: 'member.paymentHistory' },
  { key: 'order', labelKey: 'memberDetail.tabOrder' },
  { key: 'ticket', labelKey: 'memberDetail.tabTicket' },
  { key: 'coupon', labelKey: 'memberDetail.tabCoupon' },
  { key: 'address', labelKey: 'memberDetail.tabAddress' },
];

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員詳細ページ。基本情報ヘッダー + 6タブで会員のサブスク・決済・オーダー・チケット・クーポン・住所を表示する。
 * @graph-connects useMemberProfile [calls] 会員プロフィール
 * @graph-connects useMemberPayments [calls] 決済履歴
 * @graph-connects useMemberOrders [calls] オーダー履歴
 * @graph-connects useMemberTickets [calls] チケット履歴
 * @graph-connects useMemberCoupons [calls] クーポン履歴
 * @graph-connects useMemberAddresses [calls] 住所一覧
 */
export function MemberDetailPage() {
  const { t } = useTranslation();
  const { arthurUserId: rawId } = useParams({ strict: false });
  const arthurUserId = rawId ? Number(rawId) : undefined;
  const [activeTab, setActiveTab] = useState<TabKey>('sub');

  const profile = useMemberProfile(arthurUserId);
  const payments = useMemberPayments(arthurUserId);
  const orders = useMemberOrders(arthurUserId);
  const tickets = useMemberTickets(arthurUserId);
  const coupons = useMemberCoupons(arthurUserId);
  const addresses = useMemberAddresses(arthurUserId);

  const snapshotAt = profile.data?.snapshotAt ?? null;

  return (
    <div className="mx-auto max-w-[1280px] p-6">
      <Link
        to="/members"
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-[var(--color-accent-primary)] hover:underline"
      >
        ← {t('memberDetail.backToList')}
      </Link>

      <div className="mb-5 flex items-baseline gap-3">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          {t('memberDetail.title')}
        </h1>
        {snapshotAt && (
          <span className="rounded bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)]">
            {t('common.dataUpdate')}: {formatJst(snapshotAt)}
          </span>
        )}
      </div>

      {/* Profile header */}
      {profile.isLoading ? (
        <div className="mb-4 flex items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-12">
          <div className="loading-spinner" />
        </div>
      ) : profile.error ? (
        <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-12 text-center text-sm text-[var(--color-error)]">
          {t('memberDetail.profileError')}
        </div>
      ) : profile.data ? (
        <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            <ProfileField label="arthur_user_id" value={String(profile.data.arthurUserId)} mono />
            <ProfileField
              label={t('memberDetail.fullName')}
              value={
                [profile.data.lastName, profile.data.firstName].filter(Boolean).join(' ') || null
              }
            />
            <ProfileField label={t('memberList.email')} value={profile.data.email} small />
            <ProfileField label={t('memberList.tel')} value={profile.data.tel} mono />
            <div>
              <div className="mb-0.5 text-[11px] font-medium text-[var(--color-text-muted)]">
                {t('common.status')}
              </div>
              <div>
                {(() => {
                  const st = STATUS_STYLES[profile.data.subscriptionStatus ?? ''];
                  return st ? (
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{ background: st.bg, color: st.text }}
                    >
                      {st.label}
                    </span>
                  ) : (
                    <span className="text-[14px] font-medium">-</span>
                  );
                })()}
              </div>
            </div>
            <div>
              <div className="mb-0.5 text-[11px] font-medium text-[var(--color-text-muted)]">
                {t('memberDetail.plan')}
              </div>
              <div className="text-[14px] font-medium">
                {profile.data.currentPlanName ?? '-'}
                {profile.data.currentPlanPrice !== null && (
                  <span className="ml-1 text-[12px] text-[var(--color-text-secondary)]">
                    {t('memberDetail.pricePerMonth', {
                      price: formatNumber(profile.data.currentPlanPrice),
                    })}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="mb-0.5 text-[11px] font-medium text-[var(--color-text-muted)]">
                {t('memberDetail.lineLink')}
              </div>
              <div>
                <span
                  className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{
                    background: profile.data.lineLinked
                      ? 'var(--color-success-bg)'
                      : 'var(--color-bg-elevated)',
                    color: profile.data.lineLinked
                      ? 'var(--color-success)'
                      : 'var(--color-text-muted)',
                  }}
                >
                  {profile.data.lineLinked
                    ? t('memberDetail.lineLinked')
                    : t('memberDetail.lineNotLinked')}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2 border-t border-[var(--color-border)] pt-3.5">
            <a
              href={profile.data.karteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-accent-primary)] transition-colors hover:bg-[var(--color-accent-bg)]"
            >
              {t('memberDetail.openKarte')} <ExternalLink className="h-3 w-3" />
            </a>
            {profile.data.channelTalkSearchUrl && (
              <a
                href={profile.data.channelTalkSearchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-accent-primary)] transition-colors hover:bg-[var(--color-accent-bg)]"
              >
                {t('memberDetail.searchChannelTalk')} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      ) : null}

      {/* Tabs */}
      <div className="mb-4 flex gap-0 overflow-x-auto border-b border-[var(--color-border)]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setActiveTab(tab.key);
            }}
            className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-[13px] font-medium transition-colors ${
              activeTab === tab.key
                ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'sub' && (
        <>
          {profile.isLoading ? (
            <TabLoading />
          ) : profile.data ? (
            <SubscriptionTab profile={profile.data} />
          ) : null}
        </>
      )}
      {activeTab === 'pay' && (
        <>
          {payments.isLoading ? (
            <TabLoading />
          ) : payments.data ? (
            <PaymentTab data={payments.data} />
          ) : null}
        </>
      )}
      {activeTab === 'order' && (
        <>
          {orders.isLoading ? <TabLoading /> : orders.data ? <OrderTab data={orders.data} /> : null}
        </>
      )}
      {activeTab === 'ticket' && (
        <>
          {tickets.isLoading ? (
            <TabLoading />
          ) : tickets.data ? (
            <TicketTab data={tickets.data} />
          ) : null}
        </>
      )}
      {activeTab === 'coupon' && (
        <>
          {coupons.isLoading ? (
            <TabLoading />
          ) : coupons.data ? (
            <CouponTab data={coupons.data} />
          ) : null}
        </>
      )}
      {activeTab === 'address' && (
        <>
          {addresses.isLoading ? (
            <TabLoading />
          ) : addresses.data ? (
            <AddressTab data={addresses.data} />
          ) : null}
        </>
      )}
    </div>
  );
}

/** @graph-connects none */
function TabLoading() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="loading-spinner" />
    </div>
  );
}

/** @graph-connects none */
function ProfileField({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div>
      <div className="mb-0.5 text-[11px] font-medium text-[var(--color-text-muted)]">{label}</div>
      <div
        className={`text-[14px] font-medium ${mono ? 'font-mono text-[13px]' : ''} ${small ? 'text-[13px]' : ''}`}
      >
        {value ?? '-'}
      </div>
    </div>
  );
}

/** @graph-connects none */
function formatJst(isoDate: string): string {
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** @graph-connects none */
function formatNumber(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
